import { ZButton } from "@/shared/components/button";
import { Modal, ModalBody, ModalContent, Input } from "@nextui-org/react";
import { X } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createControlSet } from "../../requests";
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise";
import { getCustomizeControlsActions } from "@/modules/agent/states/cusomize-controls";
import { AgentSessionType } from "@/modules/sessions/types";

interface CreateControlSetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateControlSetModal = ({
  isOpen,
  onClose,
}: CreateControlSetModalProps) => {
  const navigate = useNavigate();
  const [controlSetName, setControlSetName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");


  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setControlSetName("");
      setErrorMessage("");
    }
  }, [isOpen]);


  const handleClose = () => {
    setControlSetName("");
    setErrorMessage("");
    onClose();
  };


  const handleProceed = async () => {
    if (!controlSetName.trim()) return;

    setIsCreating(true);
    try {
      // Call create API first
      const [error, response] = await parsePromise(createControlSet(controlSetName.trim()));
      
      if (error) {
        // Check for specific error types first
        const errorType = error?.response?.data?.error_type;
        
        // Handle error_type that might be a string or array
        const isPermissionDeniedError = Array.isArray(errorType) 
          ? errorType.includes("Permission Denied Error")
          : errorType === "Permission Denied Error";
          
        if (isPermissionDeniedError) {
          // Show user-friendly message for duplicate control set names
          setErrorMessage("Name already exists. Please choose a different name to proceed.");
          return;
        }
        
        // Extract and display the actual API error message for other errors
        let apiErrorMessage = error?.message || "An unexpected error occurred. Please try again later.";
        
        // Handle error_message that might be a string or array
        if (!apiErrorMessage || apiErrorMessage === "An unexpected error occurred. Please try again later.") {
          const errorData = error?.response?.data?.error_message;
          if (Array.isArray(errorData)) {
            apiErrorMessage = errorData[0] || "An unexpected error occurred. Please try again later.";
          } else if (typeof errorData === 'string') {
            apiErrorMessage = errorData;
          }
        }
        
        setErrorMessage(apiErrorMessage);
        return;
      }

      if (response?.success && response.control_set_state) {
        // Show success notification
        addNotification({
          type: "success",
          message: `New control set "${response.control_set_state.controls_set_name}" created successfully.`,
        });

        // Set up control set edit context with real API response data
        const { setControlSetEditContext, setDataState, setAgentType, setIsCustomControls } = getCustomizeControlsActions();
        setControlSetEditContext({
          isControlSetEdit: true,
          isNewControlSet: false, // Now exists in DB
          controlSetId: response.control_set_state.id,
          controlsUrl: response.control_set_state.controls_url,
          controlSetName: response.control_set_state.controls_set_name,
        });

        // Initialize with empty data state for new control set
        setDataState([]);
        setAgentType(AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT);
        setIsCustomControls(true);
        
        // Close modal
        handleClose();
        
        // Navigate to control set edit page
        setTimeout(() => {
          navigate('/settings/control-sets/edit');
        }, 100);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Create control set error:", error);
      // For unexpected errors (not API validation errors), show generic message
      setErrorMessage("An unexpected error occurred. Please try again later.");
    } finally {
      setIsCreating(false);
    }
  };


  const isCreateDisabled = !controlSetName.trim() || isCreating;
  

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      size="md"
      hideCloseButton={true}
      classNames={{
        base: "max-w-md",
        wrapper: "z-[9999] p-2",
      }}
    >
      <ModalContent className="max-h-[500px]">
        <ModalBody className="p-6">
              <div className="flex flex-col gap-6 w-full">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-zinc-900 text-center flex-grow">
                    Create New Controls Set
                  </h3>
                  <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>
                <div className="w-full flex flex-col items-start gap-1.5">
                  <label htmlFor="controlSetName" className="text-sm font-normal text-[#18181B]">
                    Name of Controls Set
                  </label>
                  <Input
                    id="controlSetName"
                    placeholder="Enter name of new control set"
                    value={controlSetName}
                    onChange={(e) => {
                      setControlSetName(e.target.value);
                      // Clear error message when user starts typing
                      if (errorMessage) {
                        setErrorMessage("");
                      }
                    }}
                    className="w-full"
                    classNames={{
                      inputWrapper: `bg-[#FAFAFA] border rounded-lg h-auto px-4 py-2 ${errorMessage ? 'border-[#F31260]' : 'border-[#E4E4E7]'}`,
                      input: "text-sm placeholder:text-[#A1A1AA]",
                    }}
                    variant="flat"
                    isInvalid={!!errorMessage}
                  />
                  {errorMessage && (
                    <div className="text-[#F31260] text-xs mt-1 px-1">
                      {errorMessage}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 pt-1 mt-auto">
                  <div className="w-full border-t border-[#DCDCDC]" />
                  <div className="flex justify-end gap-2 w-full pt-2">
                    <ZButton
                      variant="bordered"
                      onClick={handleClose}
                      className="h-7 px-3 text-sm rounded-md border-[#006FEE] text-[#006FEE] font-medium"
                    >
                      Cancel
                    </ZButton>
                    <ZButton
                      color="primary"
                      onClick={handleProceed}
                      isDisabled={isCreateDisabled}
                      isLoading={isCreating}
                      className="h-7 px-3 text-sm rounded-md bg-[#006FEE] text-white font-medium"
                    >
                      Proceed
                    </ZButton>
                  </div>
                </div>
              </div>
            </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateControlSetModal;